import type { ReactNode } from "react";
import { Toast as ToastPrimitive } from "@base-ui/react/toast";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "./cn";

export interface ToastCustomData {
  readonly action?: {
    readonly label: ReactNode;
    readonly onClick: () => void;
  };
}

export const toastManager = ToastPrimitive.createToastManager<ToastCustomData>();

export type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export const toast = {
  show: (
    title: ReactNode,
    options?: {
      description?: ReactNode;
      variant?: ToastVariant;
      action?: ToastCustomData["action"];
    },
  ) =>
    toastManager.add({
      title,
      description: options?.description,
      type: options?.variant ?? "default",
      data: options?.action ? { action: options.action } : undefined,
    }),
  success: (title: ReactNode, description?: ReactNode) =>
    toastManager.add({ type: "success", title, description }),
  error: (title: ReactNode, description?: ReactNode) =>
    toastManager.add({ type: "error", title, description }),
  warning: (title: ReactNode, description?: ReactNode) =>
    toastManager.add({ type: "warning", title, description }),
  info: (title: ReactNode, description?: ReactNode) =>
    toastManager.add({ type: "info", title, description }),
  close: (toastId: string) => toastManager.close(toastId),
};

function getVariantIcon(type?: string): ReactNode {
  if (!type) return null;
  switch (type) {
    case "success":
      return (
        <CheckCircle2
          size={14}
          className="text-[var(--success)] shrink-0 mt-0.5"
          aria-hidden="true"
        />
      );
    case "error":
      return (
        <AlertCircle
          size={14}
          className="text-[var(--destructive)] shrink-0 mt-0.5"
          aria-hidden="true"
        />
      );
    case "warning":
      return (
        <AlertTriangle
          size={14}
          className="text-[var(--warning)] shrink-0 mt-0.5"
          aria-hidden="true"
        />
      );
    case "info":
      return <Info size={14} className="text-[var(--info)] shrink-0 mt-0.5" aria-hidden="true" />;
    default:
      return null;
  }
}

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager<ToastCustomData>();

  return (
    <ToastPrimitive.Viewport
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none max-w-sm w-full p-2 outline-none"
      data-slot="toast-viewport"
    >
      {toasts.map((toastInstance) => {
        const icon = getVariantIcon(toastInstance.type);
        const action = toastInstance.data?.action;

        return (
          <ToastPrimitive.Root
            key={toastInstance.id}
            toast={toastInstance}
            className={cn(
              "pointer-events-auto flex w-full items-start gap-2.5 rounded-md border border-[var(--border)] bg-[var(--panel)] p-3 shadow-lg transition-all duration-200 outline-none backdrop-blur-md",
              "data-[ending-style]:opacity-0 data-[ending-style]:scale-95 data-[starting-style]:opacity-0 data-[starting-style]:translate-y-2",
            )}
            data-slot="toast"
          >
            {icon}
            <div className="flex-1 min-w-0">
              {toastInstance.title ? (
                <ToastPrimitive.Title className="text-xs font-medium text-[var(--text)] leading-tight">
                  {toastInstance.title}
                </ToastPrimitive.Title>
              ) : null}
              {toastInstance.description ? (
                <ToastPrimitive.Description className="mt-1 text-[11px] text-[var(--muted)] leading-relaxed">
                  {toastInstance.description}
                </ToastPrimitive.Description>
              ) : null}
              {action ? (
                <button
                  type="button"
                  onClick={action.onClick}
                  className="mt-2 text-[11px] font-semibold text-[var(--accent)] hover:underline focus-visible:outline-2 focus-visible:outline-[var(--ring)]"
                >
                  {action.label}
                </button>
              ) : null}
            </div>
            <ToastPrimitive.Close
              className="shrink-0 p-0.5 text-[var(--muted)] hover:text-[var(--text)] rounded-xs focus-visible:outline-2 focus-visible:outline-[var(--ring)]"
              aria-label="Dismiss notification"
            >
              <X size={12} aria-hidden="true" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        );
      })}
    </ToastPrimitive.Viewport>
  );
}

export function ToastProvider({ children }: { readonly children?: ReactNode }) {
  return (
    <ToastPrimitive.Provider toastManager={toastManager}>
      {children}
      <ToastPrimitive.Portal>
        <ToastList />
      </ToastPrimitive.Portal>
    </ToastPrimitive.Provider>
  );
}
